import { DecimalPipe } from '@angular/common';
import { Component, computed, effect, inject, input, ChangeDetectionStrategy } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FieldTree, FormField } from '@angular/forms/signals';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { forkJoin, of, tap } from 'rxjs';
import { MceApiService } from '../../../core/mceapi.client.generated';
import { appOpt } from '../../../core/mceapi.client.helpers';
import { InfoMessageComponent } from '../../../shared/components/info-message/info-message.component';
import { DialogService } from '../../../shared/dialogs/dialog.service';
import { PowerFactor } from '../common/power-factor/power-factor.model';
import { createSystemVoltageDto, isDC, SystemVoltage, VoltageCase } from '../system-voltage/system-voltage.model';
import { createFaultSource, createFaultSourceDto, FaultSource } from './fault-source.model';
import { CalculationBoxComponent } from '../../../shared/components/calculation-box/calculation-box.component';
import { CableCalculationsStore } from '../cable-calculations.store';
import { EditCable } from '../cable-edit.model';
import { createCableSelectionDto } from '../selection/cable-selection.model';
import { isNone, match, unwrap } from '../../../shared/functions/functional';
import { createEarthingDto } from '../earthing/earthing.model';

interface FaultLevel {
  i: number; // in kA
  pf: PowerFactor
  z: number; // in ohms
  xrRatio?: number
}


@Component({
    selector: 'cable-edit-fault-source',
    imports: [FormField, MatInputModule, InfoMessageComponent, CalculationBoxComponent,
            MatButtonModule, MatIconModule,DecimalPipe],
    templateUrl: './fault-source.component.html',  
})
export class FaultSourceComponent {

  readonly form = input.required<FieldTree<EditCable>>();

  private mceApiService = inject(MceApiService);
  readonly store = inject(CableCalculationsStore);
  private dialogService = inject(DialogService);


  calculateLevelRx = rxResource({
    params: () => ({
      r: this.form().faultSource.sources[0].r().value(),
      x: this.form().faultSource.sources[0].x().value(),
      re: this.form().faultSource.sources[1].r().value(),
      xe: this.form().faultSource.sources[1].x().value(),
      systemVoltage: createSystemVoltageDto(this.form().systemVoltage)(),
      valid: this.form().systemVoltage().valid() && this.form().faultSource().valid()
    }),
    stream: ({ params }) =>
      params.valid
        ? this.mceApiService.v1FaultCalculationRequestLevel({
            r: params.r,
            x: params.x,
            re: params.re,
            xe: params.xe,
            systemVoltage: params.systemVoltage
        })
       : of(undefined),
  });
  calculateLevel = computed(() => this.calculateLevelRx.value());

  impedancesRx = rxResource({
    params: () => ({
      sources: Array.from(this.form().faultSource.sources).map(source => ({
        r: source.r().value(),
        x: source.x().value()
      })),
      valid: this.form().faultSource().valid()
    }),
    stream: ({ params }) =>
      params.valid
        ? forkJoin(params.sources.map(source => this.mceApiService.v1Impedance(source.r, source.x)))
        : of(undefined)
  });

  faultLevels = computed<FaultLevel[]>(() => {
    const impedances = this.impedancesRx.value();

    return [
      {
        i: (this.calculateLevel()?.i ?? 0) * 1e-3,
        pf: { pf: this.calculateLevel()?.pf?.pf ?? 1, lagging: this.calculateLevel()?.pf?.lagging ?? true },
        z: impedances?.[0]?.z ?? 0,
        xrRatio: match<number, number | undefined>(() => undefined, value => value)(appOpt(impedances?.[0]?.xrRatio))
      },
      {
        i: (this.calculateLevel()?.ie ?? 0) * 1e-3,
        pf: { pf: this.calculateLevel()?.pFe?.pf ?? 1, lagging: this.calculateLevel()?.pFe?.lagging ?? true },
        z: impedances?.[1]?.z ?? 0,
        xrRatio: match<number, number | undefined>(() => undefined, value => value)(appOpt(impedances?.[1]?.xrRatio))
      }
    ];
  });

  cableFaultRx = rxResource({
    params: () => ({
      systemVoltage: createSystemVoltageDto(this.form().systemVoltage)(),
      selectedCable: createCableSelectionDto(this.form().selectedCable)(),
      modelFaultSource: createFaultSourceDto(this.form().faultSource)(),
      cableImpedance: this.store.impedance(),
      valid: this.form().systemVoltage().valid() && this.form().selectedCable().valid() && this.form().faultSource().valid() && this.store.impedance() !== undefined
    }),
    stream: ({ params }) => {

      if (params.cableImpedance === undefined || isNone(params.selectedCable) || !params.valid) {
        return of(undefined);
      }

      return this.mceApiService.v1Fault({
        systemVoltage: params.systemVoltage,
        selectedCable: unwrap(params.selectedCable),
        modelFaultSource: params.modelFaultSource,
        cableImpedance: params.cableImpedance
      });

    }
  });
  cableFault = computed(() => this.cableFaultRx.value());



  voltageChange = effect(() => {
    const voltageCase =
      this.form().systemVoltage.case().value() !== VoltageCase.TPN
        ? this.form().systemVoltage.case().value()
        : VoltageCase.AC3ph;
    if (this.form().faultSource.sourceType().value() !== voltageCase) {
      this.form().faultSource.sourceType().value.set(voltageCase);
    }
  });



  viewState = computed(() => ({
    isDC: isDC(this.form().systemVoltage)(),
    disableImpedance: false,
    loading: this.calculateLevelRx.isLoading() || this.impedancesRx.isLoading() || this.cableFaultRx.isLoading(),

    status: this.cableFault()?.cableWithstand?.status ?? { case: 'Success', message: '' }
  }));


  async onClickSc() {
  const result = await this.dialogService.shortCircuitKa({ title: 'Short Circuit Fault Level', isDC: this.viewState().isDC });
  if (!result) return;

  this.mceApiService.v1FaultCalculationRequestImpedance({
    i: result.i * 1e3, // kA → A
    pf: { pf: result.pf.pf, lagging: result.pf.lagging },
    ie: this.faultLevels()[1].i * 1e3, // kA → A
    pFe: { pf: this.faultLevels()[1].pf.pf, lagging: this.faultLevels()[1].pf.lagging },
    systemVoltage: createSystemVoltageDto(this.form().systemVoltage)(),
    earthing: createEarthingDto(this.form().earthing)(),
  }).pipe(
    tap(x => {
      if (x.r === undefined || x.x === undefined) {
        throw new Error('Fault impedance response is missing short-circuit values.');
      }

      this.form().faultSource.sources[0].r().value.set(x.r.roundTo(6));
      this.form().faultSource.sources[0].x().value.set(x.x.roundTo(6));
    })
  ).subscribe();
  }

  async onClickScZe() {
    const result = await this.dialogService.impedanceZXRRatio({ title: 'Short Circuit Impedance' });
    if (!result) return;

    this.mceApiService.v1ImpedanceMagnitudeXrRatio(result.z, result.xrRatio).pipe(
      tap(x => {
        if (x.r === undefined || x.x === undefined) {
          throw new Error('Impedance response is missing short-circuit values.');
        }

        this.form().faultSource.sources[0].r().value.set(x.r.roundTo(6));
        this.form().faultSource.sources[0].x().value.set(x.x.roundTo(6));
      })
    ).subscribe();
  }

  async onClickEf() {
    const result = await this.dialogService.shortCircuitKa({ title: 'Earth Fault Level', isDC: this.viewState().isDC });
    if (!result) return;

    this.mceApiService.v1FaultCalculationRequestImpedance({
      i: this.faultLevels()[0].i * 1e3, // kA → A
      pf: { pf: this.faultLevels()[0].pf.pf, lagging: this.faultLevels()[0].pf.lagging },
      ie: result.i * 1e3, // kA → A
      pFe: { pf: result.pf.pf, lagging: result.pf.lagging },
      systemVoltage: createSystemVoltageDto(this.form().systemVoltage)(),
      earthing: createEarthingDto(this.form().earthing)(),
    }).pipe(
      tap(x => {
        if (x.re === undefined || x.xe === undefined) {
          throw new Error('Fault impedance response is missing earth-fault values.');
        }

        this.form().faultSource.sources[1].r().value.set(x.re.roundTo(6));
        this.form().faultSource.sources[1].x().value.set(x.xe.roundTo(6));
      })
    ).subscribe();
  }

  async onClickEfZe() {
    const result = await this.dialogService.impedanceZXRRatio({ title: 'Earth Fault Impedance' });
    if (!result) return;

    this.mceApiService.v1ImpedanceMagnitudeXrRatio(result.z, result.xrRatio).pipe(
      tap(x => {
        if (x.r === undefined || x.x === undefined) {
          throw new Error('Impedance response is missing earth-fault values.');
        }

        this.form().faultSource.sources[1].r().value.set(x.r.roundTo(6));
        this.form().faultSource.sources[1].x().value.set(x.x.roundTo(6));
      })
    ).subscribe();
  }

}
